import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Image,
  Button,
  Space,
  Modal,
  Input,
  message,
  Tag,
  Row,
  Col,
  Typography,
  Select,
  Tooltip,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import LayoutPage from "../../../layouts/Main";
import {
  getPendingProducts,
  approveProduct,
  rejectProduct,
} from "../../../utils/api/productValidationService";
import "../../../scss/ProductValidation.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ProductValidation = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const initialFetchDone = useRef(false);

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role !== "1") {
      navigate("/error/403");
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  const loadProducts = useCallback(async () => {
    if (!isAuthorized) return;

    setLoading(true);
    try {
      const response = await getPendingProducts();
      const fetchedProducts = response.data || [];
      setProducts(fetchedProducts);
      setPagination((prev) => ({
        ...prev,
        total: fetchedProducts.length,
      }));
      initialFetchDone.current = true;
    } catch (error) {
      message.error("Error al cargar los productos pendientes");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthorized]);

  useEffect(() => {
    if (isAuthorized && !initialFetchDone.current) {
      loadProducts();
    }
  }, [isAuthorized, loadProducts]);

  const handleApprove = async (product) => {
    Modal.confirm({
      title: "Confirmar aprobación",
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro de aprobar el producto "${product.name}"? Este producto se publicará inmediatamente en el marketplace.`,
      okText: "Sí, aprobar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await approveProduct(product.id);
          message.success(`Producto "${product.name}" aprobado exitosamente`);
          loadProducts();
        } catch (error) {
          message.error("Error al aprobar el producto");
        }
      },
    });
  };

  const showRejectModal = (product) => {
    setSelectedProduct(product);
    setRejectReason("");
    setRejectModalVisible(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning("Por favor, ingresa un motivo de rechazo");
      return;
    }

    try {
      await rejectProduct(selectedProduct.id, rejectReason);
      message.success(
        `Producto "${selectedProduct.name}" rechazado y eliminado`
      );
      setRejectModalVisible(false);
      setSelectedProduct(null);
      setRejectReason("");
      loadProducts();
    } catch (error) {
      message.error("Error al rechazar el producto");
    }
  };

  const handlePreview = (product) => {
    setSelectedProduct(product);
    setPreviewModalVisible(true);
  };

  const handleResetFilters = () => {
    setSearchText("");
    setFilterSupplier("all");
    setPagination((prev) => ({ ...prev, current: 1 }));
    message.info("Todos los filtros han sido restablecidos");
  };

  const filteredProducts = products.filter((product) => {
    const supplierName = product.supplierName || "";
    const supplierId = String(product.supplierId || "");
    const matchesSearch =
      product.name.toLowerCase().includes(searchText.toLowerCase()) ||
      supplierName.toLowerCase().includes(searchText.toLowerCase());
    const matchesSupplier =
      filterSupplier === "all" || supplierId === String(filterSupplier);
    return matchesSearch && matchesSupplier;
  });

  const suppliers = [
    ...new Map(
      products.map((p) => [
        p.supplierId,
        { id: p.supplierId, name: p.supplierName },
      ])
    ).values(),
  ];

  const columns = [
    {
      title: "Imagen",
      dataIndex: "imageUrl",
      key: "image",
      width: 100,
      render: (imageUrl) => (
        <div className="product-image-cell">
          <Image
            src={imageUrl}
            alt="Producto"
            className="product-image"
            fallback="https://via.placeholder.com/60?text=Sin+imagen"
          />
        </div>
      ),
    },
    {
      title: "Nombre del Producto",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            SKU: {record.sku || "N/A"}
          </Text>
        </div>
      ),
    },
    {
      title: "Precio",
      dataIndex: "price",
      key: "price",
      width: 150,
      sorter: (a, b) => a.price - b.price,
      render: (price) => {
        const isPriceHigh = price > 10000000;
        return (
          <div>
            <Text
              strong
              className={isPriceHigh ? "price-high" : ""}
              style={{ fontSize: 16 }}
            >
              ${price?.toLocaleString() || 0}
            </Text>
            {isPriceHigh && (
              <Tag color="red" className="price-warning">
                ¡Precio muy alto!
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Proveedor",
      dataIndex: "supplierName",
      key: "supplier",
      width: 180,
      render: (text, record) => (
        <div>
          <Text>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            ID: {record.supplierId}
          </Text>
        </div>
      ),
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) =>
        date ? new Date(date).toLocaleDateString("es-ES") : "N/A",
    },
    {
      title: "Acciones",
      key: "actions",
      width: 250,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
              size="small"
            >
              Vista previa
            </Button>
          </Tooltip>
          <Tooltip title="Aprobar producto">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApprove(record)}
              size="small"
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Aprobar
            </Button>
          </Tooltip>
          <Tooltip title="Rechazar producto">
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => showRejectModal(record)}
              size="small"
            >
              Rechazar
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!isAuthorized) {
    return (
      <LayoutPage>
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spin size="large" tip="Verificando permisos..." />
        </div>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <div className="product-validation-card">
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <Title level={3}>Validación de Productos</Title>
            <Text type="secondary">
              Revisa y aprueba los productos antes de publicarlos en el
              marketplace
            </Text>
          </Col>

          {/* Filtro de búsqueda */}
          <Col xs={24} md={8} lg={6}>
            <Input
              placeholder="Buscar por nombre o proveedor..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>

          {/* Filtro por proveedor */}
          <Col xs={24} md={8} lg={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filtrar por proveedor"
              value={filterSupplier}
              onChange={setFilterSupplier}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">Todos los proveedores</Option>
              {suppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Botón de reset */}
          <Col xs={24} md={8} lg={6}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleResetFilters}
              style={{ width: "100%" }}
            >
              Restablecer Filtros
            </Button>
          </Col>

          {/* Badge de productos pendientes */}
          <Col xs={24}>
            <Tag
              color="orange"
              style={{ fontSize: 14, padding: "4px 12px", marginBottom: 16 }}
            >
              {filteredProducts.length} productos pendientes
            </Tag>
          </Col>

          {/* Tabla de productos */}
          <Col xs={24}>
            <div className="table-container">
              <Table
                className="styled-table"
                columns={columns}
                dataSource={filteredProducts}
                loading={loading}
                rowKey="id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: filteredProducts.length,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} de ${total} productos`,
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current: page,
                      pageSize,
                    }));
                  },
                }}
                scroll={{ x: 1000 }}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* Modal de rechazo */}
      <Modal
        title="Rechazar Producto"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
        okText="Rechazar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        {selectedProduct && (
          <div>
            <Paragraph>
              <Text strong>Producto:</Text> {selectedProduct.name}
            </Paragraph>
            <Paragraph>
              <Text strong>Proveedor:</Text> {selectedProduct.supplierName}
            </Paragraph>
            <Paragraph>
              <Text strong>Precio:</Text> $
              {selectedProduct.price?.toLocaleString()}
            </Paragraph>
            <Paragraph>
              <Text strong>Motivo de rechazo:</Text>
              <TextArea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Indica el motivo por el cual este producto no puede ser publicado..."
                style={{ marginTop: 8 }}
              />
            </Paragraph>
          </div>
        )}
      </Modal>

      {/* Modal de vista previa */}
      <Modal
        title="Vista Previa del Producto"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Cerrar
          </Button>,
          selectedProduct && (
            <Button
              key="approve"
              type="primary"
              onClick={() => {
                handleApprove(selectedProduct);
                setPreviewModalVisible(false);
              }}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Aprobar producto
            </Button>
          ),
          selectedProduct && (
            <Button
              key="reject"
              danger
              onClick={() => {
                setPreviewModalVisible(false);
                showRejectModal(selectedProduct);
              }}
            >
              Rechazar producto
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedProduct && (
          <div>
            <Row gutter={24}>
              <Col span={12}>
                <Image
                  src={selectedProduct.imageUrl}
                  alt={selectedProduct.name}
                  style={{ width: "100%", borderRadius: 8 }}
                  fallback="https://via.placeholder.com/400?text=Sin+imagen"
                />
              </Col>
              <Col span={12}>
                <Title level={4}>{selectedProduct.name}</Title>
                <Title
                  level={3}
                  style={{
                    color:
                      selectedProduct.price > 10000000 ? "#ff4d4f" : "#52c41a",
                  }}
                >
                  ${selectedProduct.price?.toLocaleString()}
                  {selectedProduct.price > 10000000 && (
                    <Tag color="red" style={{ marginLeft: 12, fontSize: 12 }}>
                      Precio excesivo
                    </Tag>
                  )}
                </Title>
                <Paragraph>
                  <Text strong>Proveedor:</Text> {selectedProduct.supplierName}
                </Paragraph>
                <Paragraph>
                  <Text strong>SKU:</Text> {selectedProduct.sku || "N/A"}
                </Paragraph>
                <Paragraph>
                  <Text strong>Categoría:</Text>{" "}
                  {selectedProduct.category || "Sin categoría"}
                </Paragraph>
                <Paragraph>
                  <Text strong>Stock:</Text>{" "}
                  {selectedProduct.stock || "No especificado"}
                </Paragraph>
                <Paragraph>
                  <Text strong>Descripción:</Text>
                  <br />
                  {selectedProduct.description || "Sin descripción"}
                </Paragraph>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </LayoutPage>
  );
};

export default ProductValidation;
